import React, { useEffect, useState } from "react";
import {
  authorizedGet,
  authorizedPost,
} from "../../Config/networkWithToken";
import configuration from "../../Config/configuration";
import MessageBar, {
  config,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
  successNotify,
} from "../../utils/helper";
import Spinner from "../../Components/Spinner/Spinner";
import { useNavigate } from "react-router-dom";
import { maritalStatusOption } from "../../utils/data";
import Strings from "../../utils/Strings";
import { useQueryClient } from "@tanstack/react-query";
import { USERS_QUERY_KEY } from "../../Admin/Pages/Matrimony/useMatrimonyUsers";

const Settings = ({ userData, userId, onClose, onStatusChange }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [maritalStatus, setMaritalStatus] = useState(
    userData?.maritalStatus || "",
  );
  const [marriedLocation, setMarriedLocation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const navigate = useNavigate();
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");
  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [status, setStatus] = useState(userData?.userStatus?.status || "");
  const [marriageDate, setMarriageDate] = useState("");
  const [selectedSpouse, setSelectedSpouse] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [selectedSpouseName, setSelectedSpouseName] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const userGender = userData?.gender;
  const [searchQuery, setSearchQuery] = useState("");
  const [otherSpouseName, setOtherSpouseName] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteReasonError, setDeleteReasonError] = useState("");

  const filterUsers = (data, userGender) => {
    if (!data) return [];

    const oppositeGender =
      userGender === "male"
        ? "female"
        : userGender === "female"
          ? "male"
          : null;

    return oppositeGender
      ? data.filter((user) => user.gender === oppositeGender)
      : data;
  };

  const fetchAllUserData = async () => {
    try {
      const res = await authorizedGet(
        `${configuration.apis.filter}?userId=${userId}`,
        config,
      );

      const response = res?.data;
      if (response?.status && Array.isArray(response?.data)) {
        const genderFiltered = filterUsers(response.data, userGender);
        setAllUsers(genderFiltered);
      } else {
        setAllUsers([]);
      }
    } catch (err) {
      console.error("Error fetching all user data:", err);
      setAllUsers([]);
    }
  };

  useEffect(() => {
    setIsBlocked(userData?.isBlocked);
    fetchAllUserData();

    setMaritalStatus(userData?.maritalStatus || "");
    setStatus(userData?.userStatus?.status || "");

    if (userData?.maritalStatus === "married") {
      fetchMarriageDetails();
    }
  }, [userData]);

  const fetchMarriageDetails = async () => {
    try {
      const res = await authorizedGet(
        `${configuration.apis.getAllMarried}?userId=${userId}`,
        config,
      );
      const response = res?.data;
      const list = response?.marriedUsers || [];
      const matchedEntries = list.filter(
        (item) => item?.userId?._id === userId,
      );
      const entry = matchedEntries.sort(
        (a, b) => new Date(b.changedAt) - new Date(a.changedAt),
      )[0];
      if (entry) {
        setMarriageDate(
          entry?.marriedDate ? entry.marriedDate.split("T")[0] : "",
        );

        if (entry?.isGuided) {
          setMarriedLocation("guided");
          if (entry?.spouse?.name) {
            setSelectedSpouse(entry?.spouse?._id || "");
            setSelectedSpouseName(entry?.spouse?.name || "");
          }
        } else if (entry?.marriedViaVmb) {
          setMarriedLocation("inside");
          setSelectedSpouse(entry?.spouse?._id || "");
          setSelectedSpouseName(entry?.spouse?.name || "");
        } else {
          setMarriedLocation("outside");
        }
      } else {
      }
    } catch (err) {
      console.error("ERROR fetching marriage details:", err);
    }
  };
  const handleDeleteUser = async () => {
    setDeleting(true);
    try {
      const res = await authorizedPost(
        `${configuration.apis.deleteUser}/${userId}`,
        { adminId, reason: deleteReason },
        config,
      );
      const response = res?.data;
      if (response?.status) {
        successNotify(response?.message);
        sessionStorage.setItem("matrimonyNeedsRefetch", "true");
        navigate("/matrimony");
      }
    } catch (error) {
      errorNotify(
        error?.response?.message || "An error occurred while deleting the user",
      );
    } finally {
      setDeleting(false);
      setShowDeleteConfirmation(false);
      setDeleteReason("");
      setDeleteReasonError("");
    }
  };

  const handleUpdateMarital = async () => {
    setUpdating(true);
    const data = {
      status: maritalStatus,
      userId: userId,
      adminId: adminId,
      marriedDate: marriageDate,
      spouse: selectedSpouse,
      marriedViaVmb: marriedLocation === "inside" ? true : false,
    };
    if (marriedLocation === "guided") {
      data.isGuided = true;

      if (selectedSpouseName === "others") {
        data.guidedUser = otherSpouseName;
      } else {
        data.guidedUserId = selectedSpouse;
      }
    }
    try {
      const res = await authorizedPost(
        configuration.apis.updateMarital,
        data,
        config,
      );
      const response = res?.data;
      if (response?.status) {
        successNotify(response?.message);
        window.location.reload();
      }
    } catch (error) {
      errorNotify(
        error?.response?.message ||
        "An error occurred while updating marital status",
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleBlockUser = async (status) => {
    setBlocking(true);
    const data = { status: status, userId, adminId, reason };
    try {
      const res = await authorizedPost(
        configuration.apis.blockUser,
        data,
        config,
      );
      if (res?.data?.status) {
        successNotify(res?.data?.message);
        setIsBlocked(!isBlocked);
        onClose();
        setShowBlockConfirmation(false);
      } else {
        errorNotify(res?.data?.message);
        setShowBlockConfirmation(false);
      }
    } catch (error) {
      errorNotify(
        error?.response?.message || "An error occurred while blocking user",
      );
    } finally {
      setBlocking(false);
      setShowBlockConfirmation(false);
    }
  };
  const handleReasonChange = (e) => {
    const input = e.target.value;
    if (activeTab === 2) {
      setReason(input);
      setError(
        input.trim().length < 15
          ? "Reason must be at least 15 characters."
          : "",
      );
    } else if (activeTab === 3) {
      setStatusReason(input);
      setStatusError(
        input.trim().length < 15
          ? "Reason must be at least 15 characters."
          : "",
      );
    }
  };
  const handleBlock = () => {
    setShowBlockConfirmation(true);
  };

  const handleChangeUserStatus = async () => {
    try {
      if (
        status &&
        (status === "active" || (statusReason && statusReason?.length > 15))
      ) {
        setUpdating(true);
        const data = {
          adminId: adminId,
          userId: userId,
          status: status,
          reason: statusReason,
        };

        const res = await authorizedPost(
          configuration.apis.userChangeStatus,
          data,
          config,
        );

        const response = res?.data;

        if (response?.status) {
          successNotify(response?.message);
          onStatusChange();
          onClose();
        } else {
          errorNotify(response?.message);
        }
      } else {
        setStatusError(
          status
            ? "Reason must be at least 15 characters."
            : "select the status.",
        );
      }
    } catch (error) {
      setError(error.message);
      console.log(error);
    } finally {
      setUpdating(false);
    }
  };

  const CloseButton = () => (
    <button onClick={onClose} className="rounded bg-gray-300 px-4 py-2">
      Close
    </button>
  );

  const handleModel = () => {
    if (reason.length === 0) {
      setError("Please enter the reason.");
    } else if (reason.length < 15) {
      setError("Reason must be at least 15 characters.");
    } else {
      setShowBlockConfirmation(true);
    }
  };

  const handleDeleteClick = () => {
    if (!deleteReason.trim()) {
      setDeleteReasonError("Please enter the reason.");
      return;
    }
    if (deleteReason.trim().length < 15) {
      setDeleteReasonError("Reason must be at least 15 characters.");
      return;
    }
    setDeleteReasonError("");
    setShowDeleteConfirmation(true);
  };

  const tabList = [
    "Marital Status",
    "Delete Account",
    "Blocked User",
    "User Status",
  ];

  return (
    <div className="font-jost fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative max-h-[90vh] w-[80%] overflow-y-auto rounded-lg bg-white p-6 shadow-lg sm:px-4 md:w-[65%] lg:w-[55%] xl:w-[45%] xxl:w-[40%]">
        {/* Tab navigation */}
        <div className="mb-4 flex justify-between border-b-2">
          <ul className="flex flex-row flex-wrap justify-center ">
            {tabList.map((list, index) => (
              <li
                key={index}
                className={`mt-3 cursor-pointer px-4 py-2 text-center text-textGreen transition-all  
                    ${activeTab === index
                    ? "border-b-2 border-textYellow text-textYellow "
                    : "hover:bg-gray-100 hover:text-textYellow"
                  }`}
                onClick={() => setActiveTab(index)}
              >
                {list}
              </li>
            ))}
          </ul>
        </div>

        {activeTab === 0 && (
          <div>
            <label className="mb-2 block">Marital Status:</label>
            <select
              value={maritalStatus}
              onChange={(e) => setMaritalStatus(e.target.value)}
              className="mb-4 w-full rounded border border-gray-300 px-3 py-2"
            >
              <option value="">
                -- Select Marital Status --
              </option>
              {maritalStatusOption.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {maritalStatus === "married" && (
              <>
                <div className="mb-4">
                  <label className="mb-2 block">Married Through:</label>
                  <select
                    value={marriedLocation}
                    onChange={(e) => setMarriedLocation(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2"
                  >
                    <option value="" disabled>
                      -- Select Married Through --
                    </option>
                    <option value="inside">Married Within VMB</option>
                    <option value="outside">Married Outside VMB</option>
                    <option value="guided">VMB Guided</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="mb-2 block">Marriage Date:</label>
                  <input
                    type="date"
                    value={marriageDate}
                    onChange={(e) => setMarriageDate(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2"
                  />
                </div>

                {marriedLocation === "inside" && (
                  <div className="mb-4">
                    <div className="mb-2 flex items-center justify-between">
                      <label className="block">Spouse Name:</label>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-48 rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                    </div>

                    <div className="relative">
                      <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="w-full rounded border border-gray-300 px-3 py-2 text-left"
                      >
                        {selectedSpouseName || "-- Select Spouse Name --"}
                      </button>

                      {dropdownOpen && (
                        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded border border-gray-300 bg-white shadow">
                          {allUsers
                            ?.filter((user) =>
                              user?.name
                                ?.toLowerCase()
                                .includes(searchQuery.toLowerCase()),
                            )
                            ?.map((user) => (
                              <li
                                key={user._id}
                                onClick={() => {
                                  setSelectedSpouse(user._id);
                                  setSelectedSpouseName(user.name);
                                  setDropdownOpen(false);
                                }}
                                className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                              >
                                {user.name}
                              </li>
                            ))}
                          {allUsers?.filter((user) =>
                            user?.name
                              ?.toLowerCase()
                              .includes(searchQuery.toLowerCase()),
                          )?.length === 0 && (
                              <li className="px-4 py-2 text-gray-500">
                                No users found
                              </li>
                            )}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
                {marriedLocation === "guided" && (
                  <div className="mb-4">
                    <div className="mb-2 flex items-center justify-between">
                      <label className="block">Spouse Name:</label>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-48 rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                    </div>

                    <div className="relative">
                      <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="w-full rounded border border-gray-300 px-3 py-2 text-left"
                      >
                        {selectedSpouseName || "-- Select Spouse Name --"}
                      </button>

                      {dropdownOpen && (
                        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded border border-gray-300 bg-white shadow">
                          {allUsers
                            ?.filter((user) =>
                              user?.name
                                ?.toLowerCase()
                                .includes(searchQuery.toLowerCase()),
                            )
                            ?.map((user) => (
                              <li
                                key={user._id}
                                onClick={() => {
                                  setSelectedSpouse(user._id);
                                  setSelectedSpouseName(user.name);
                                  setDropdownOpen(false);
                                }}
                                className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                              >
                                {user.name}
                              </li>
                            ))}
                          <li
                            onClick={() => {
                              setSelectedSpouseName("others");
                              setDropdownOpen(false);
                            }}
                            className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                          >
                            Others
                          </li>
                          {allUsers?.filter((user) =>
                            user?.name
                              ?.toLowerCase()
                              .includes(searchQuery.toLowerCase()),
                          )?.length === 0 && (
                              <li className="px-4 py-2 text-gray-500">
                                No users found
                              </li>
                            )}
                        </ul>
                      )}
                    </div>
                    {selectedSpouseName === "others" && (
                      <input
                        type="text"
                        placeholder="Enter Spouse Name"
                        value={otherSpouseName}
                        onChange={(e) => setOtherSpouseName(e.target.value)}
                        className="mt-2 w-full rounded border border-gray-300 px-3 py-2"
                      />
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between">
              <button
                onClick={handleUpdateMarital}
                className="rounded bg-blue-500 px-4 py-2 text-white"
                disabled={updating}
              >
                {updating ? <Spinner size="small" /> : "Update"}
              </button>
              <CloseButton />
            </div>
          </div>
        )}


        {activeTab === 1 && (
          <div>
            <p className="mb-4 text-center text-sm text-gray-700">
              {Strings.USER_DELETE}
            </p>


            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Reason for Deletion <span className="text-red-500">*</span>
              </label>
              <textarea
                rows="3"
                placeholder="Enter reason for deleting this account... (min 15 characters)"
                value={deleteReason}
                onChange={(e) => {
                  setDeleteReason(e.target.value);
                  setDeleteReasonError(
                    e.target.value.trim().length < 15
                      ? "Reason must be at least 15 characters."
                      : "",
                  );
                }}
                className="w-full resize-none rounded-md border border-gray-300 p-2 text-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
              />
              {deleteReasonError && (
                <p className="mt-1 text-xs text-red-600">{deleteReasonError}</p>
              )}
            </div>

            <div className="mt-4 flex justify-between">
              <button
                onClick={handleDeleteClick}
                className="rounded bg-red-500 px-4 py-2 text-white disabled:opacity-50"
                disabled={deleting}
              >
                {deleting ? <Spinner size="small" /> : "Delete"}
              </button>
              <CloseButton />
            </div>
          </div>
        )}


        {activeTab === 2 && (
          <div>
            {isBlocked === true ? (
              <div>
                <p className="text-center">{Strings.USER_UNBLOCK}</p>
                <div className="mt-4 flex justify-between">
                  <button
                    onClick={handleBlock}
                    className="rounded bg-blue-500 px-4 py-2 text-white"
                    disabled={blocking}
                  >
                    {blocking ? <Spinner size="small" /> : "Unblock"}
                  </button>
                  <CloseButton />
                </div>
              </div>
            ) : (
              <div>
                <label className="mb-2 block">Reason:</label>
                <textarea
                  placeholder="Enter reason..."
                  rows="3"
                  className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500"
                  value={reason}
                  onChange={handleReasonChange}
                ></textarea>
                {error && (
                  <p className="mt-2 text-left text-xs text-red-600">{error}</p>
                )}
                <p className="mt-2 text-center text-xs text-red-600"></p>
                <div className="mt-4 flex justify-between">
                  <button
                    onClick={handleModel}
                    className={`rounded px-4 py-2 text-white ${blocking ? "bg-gray-400" : "bg-red-500"}`}
                    disabled={blocking}
                  >
                    {blocking ? <Spinner size="small" /> : "Block"}
                  </button>
                  <CloseButton />
                </div>
              </div>
            )}
          </div>
        )}


        {activeTab === 3 && (
          <div>
            <div className="mb-4">
              <label className="mb-2 block">Status :</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2"
              >
                <option value="" disabled>
                  -- Select Status --
                </option>
                <option value="notInterest">Not Interested</option>
                <option value="noResponse">No Response</option>
                {(userData?.isNotInterested ||
                  userData?.isNoResponse ||
                  userData?.userStatus?.status === "active") && (
                    <option value="active">Active</option>
                  )}
              </select>
              {status !== "active" && (
                <>
                  <label className="mb-2 block">Reason :</label>
                  <textarea
                    placeholder="Enter reason..."
                    rows="3"
                    className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500"
                    value={statusReason}
                    onChange={handleReasonChange}
                  ></textarea>
                  {statusError && (
                    <p className="mt-2 text-left text-xs text-red-600">
                      {statusError}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-between">
              <button
                onClick={handleChangeUserStatus}
                className="rounded bg-blue-500 px-4 py-2 text-white"
                disabled={updating}
              >
                {updating ? <Spinner size="small" /> : "Update"}
              </button>
              <CloseButton />
            </div>
          </div>
        )}


        {showDeleteConfirmation && (
          <div className="fixed inset-0 z-50 flex w-full items-center justify-center bg-black bg-opacity-40">
            <div className="w-[90%] rounded-lg bg-white p-4 shadow-lg sm:w-[60%] md:w-[40%]">
              <p className="mb-2 text-center text-sm font-semibold sm:text-base">
                {Strings.DELETE_PERMANTANTLY}
              </p>
              <p className="mb-4 rounded bg-red-50 px-3 py-2 text-center text-xs text-red-600">
                Reason: {deleteReason}
              </p>
              <div className="flex justify-around">
                <button
                  onClick={handleDeleteUser}
                  className="rounded bg-red-500 px-4 py-2 text-white"
                  disabled={deleting}
                >
                  {deleting ? <Spinner size="small" /> : "Yes, Delete"}
                </button>
                <button
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="rounded bg-gray-300 px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}


        {showBlockConfirmation && (
          <div className="fixed inset-0 z-50 flex w-full items-center justify-center bg-opacity-50">
            <div className="w-[90%] rounded-lg bg-white p-4 shadow-lg sm:w-[70%] md:w-[60%]">
              <p className="mb-4 text-center text-sm sm:text-base">
                {isBlocked
                  ? Strings.UNBLOCK_PERMANENTLY
                  : Strings.BLOCK_PERMANTANTLY}
              </p>
              <div className="flex justify-around">
                {isBlocked ? (
                  <button
                    onClick={() => handleBlockUser("unblock")}
                    className={`rounded px-4 py-2 text-white ${isBlocked ? "bg-blue-500" : "bg-red-500"}`}
                    disabled={blocking}
                  >
                    {blocking ? <Spinner size="small" /> : "Yes, Unblock"}
                  </button>
                ) : (
                  <button
                    onClick={() => handleBlockUser("block")}
                    className={`rounded px-4 py-2 text-white ${isBlocked ? "bg-blue-500" : "bg-red-500"}`}
                    disabled={blocking}
                  >
                    {blocking ? <Spinner size="small" /> : "Yes, Block"}
                  </button>
                )}
                <button
                  onClick={() => setShowBlockConfirmation(false)}
                  className="rounded bg-gray-300 px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
